import {FormControl, FormField, MultiChoiceProps, Option} from 'platform/components';
import {Grid, GridItem, Hide, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {FieldValues} from 'react-hook-form';

import {head, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetVehicleSelectionAttributesQuery} from '@dms/api/sales';
import {CampaignFilter} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {useTenant} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useVehicleSelectionFilterOptions} from '../../hooks/useVehicleSelectionFilterOptions';
import {VehicleSelectionFormBinding} from '../../types/VehicleSelectionFormBinding';
import {VehicleSelectionOperator} from '../../types/VehicleSelectionOperator';
import {getVehicleSelectionAttributeLabel} from '../../utils/getVehicleSelectionAttributeLabel';
import {VehicleSelectionListField} from './components/VehicleSelectionListField';
import {VehicleSelectionPriceField} from './components/VehicleSelectionPriceField';
import {
  VEHICLE_SELECTION_LIST_ATTRIBUTES,
  VehicleSelectionListAttribute,
} from './constants/VEHICLE_SELECTION_LIST_ATTRIBUTES';

const VEHICLE_TYPE_ATTRIBUTE = 'VEHICLE_TYPE';

// Their options come from the vehicle type's catalogue, so they are cleared with it.
const VEHICLE_TYPE_DEPENDENT_ATTRIBUTES = [
  'MAKE',
  'MODEL_FAMILY',
  'MODEL',
  'BODY_STYLE',
  'FUEL_TYPE',
  'CONDITION',
] as const satisfies readonly VehicleSelectionListAttribute[];

const MAKE_DEPENDENT_ATTRIBUTES = [
  'MODEL_FAMILY',
  'MODEL',
] as const satisfies readonly VehicleSelectionListAttribute[];

type OperatorAttribute = VehicleSelectionListAttribute | typeof VEHICLE_TYPE_ATTRIBUTE;

type ListFieldPresentation = {
  label: string;
  options: MultiChoiceProps<string | number>['options'];
  isLoading?: boolean;
  isDisabled?: boolean;
  tooltip?: string;
  onChange?: () => void;
};

interface VehicleSelectionFilterFieldsProps<
  TFieldValues extends FieldValues,
> extends RequiredTestIdProps {
  control: FormControl<TFieldValues>;
  binding: VehicleSelectionFormBinding<TFieldValues>;
  // The current rules, watched by the owning form: they drive the dependent options and the
  // operator of every list attribute.
  filter: CampaignFilter | Nullish;
  isDisabled?: boolean;
  // Offers "everything except" per list attribute and "value is missing", both driven by the
  // vehicle selection attribute catalog. The campaign form keeps the include-only editor.
  areExtendedOperatorsEnabled?: boolean;
}

// The condition fields of a vehicle selection, shared by the campaign and the promotional photo
// form. A field left empty means "any"; the owning form decides when the rules apply at all.
export function VehicleSelectionFilterFields<TFieldValues extends FieldValues>(
  props: VehicleSelectionFilterFieldsProps<TFieldValues>
) {
  const {tenantCurrency} = useTenant();

  const mileageSuffix = i18n.t('general.metric.km');
  const daysSuffix = i18n.t('general.metric.d');

  const filterOptions = useVehicleSelectionFilterOptions(
    props.filter?.vehicleType,
    props.filter?.makes
  );

  const {data: attributes} = useGetVehicleSelectionAttributesQuery(undefined, {
    skip: !props.areExtendedOperatorsEnabled,
  });

  // The operator follows the stored values; the override only counts while both lists are empty.
  const [operatorOverrides, setOperatorOverrides] = useState<
    Partial<Record<OperatorAttribute, VehicleSelectionOperator>>
  >({});

  const canExclude = (attribute: OperatorAttribute) =>
    (props.areExtendedOperatorsEnabled ?? false) &&
    isNotNil(attributes?.find((item) => item.attribute === attribute)?.excludeField);

  const getOperator = (
    attribute: OperatorAttribute,
    includedValues: string[] | Nullish,
    excludedValues: string[] | Nullish
  ) =>
    match({
      hasIncluded: isNotNilOrEmpty(includedValues),
      hasExcluded: isNotNilOrEmpty(excludedValues),
    })
      .with({hasExcluded: true}, () => VehicleSelectionOperator.EXCLUDED)
      .with({hasIncluded: true}, () => VehicleSelectionOperator.INCLUDED)
      .otherwise(() => operatorOverrides[attribute] ?? VehicleSelectionOperator.INCLUDED);

  const getListOperator = (attribute: VehicleSelectionListAttribute) => {
    const {includeField, excludeField} = VEHICLE_SELECTION_LIST_ATTRIBUTES[attribute];

    return getOperator(attribute, props.filter?.[includeField], props.filter?.[excludeField]);
  };

  const vehicleType = props.filter?.vehicleType;
  const vehicleTypeOperator = getOperator(
    VEHICLE_TYPE_ATTRIBUTE,
    isNotNil(vehicleType) ? [vehicleType] : [],
    props.filter?.excludedVehicleTypes
  );

  const clearListAttributes = (attributesToClear: readonly VehicleSelectionListAttribute[]) =>
    attributesToClear.forEach((attribute) => {
      const {includeField, excludeField} = VEHICLE_SELECTION_LIST_ATTRIBUTES[attribute];

      props.binding.setListFieldValue(includeField, []);
      // The campaign never writes the exclusion lists, so its payload stays as it was.
      if (props.areExtendedOperatorsEnabled) {
        props.binding.setListFieldValue(excludeField, []);
      }
    });

  const onVehicleTypeChange = () => clearListAttributes(VEHICLE_TYPE_DEPENDENT_ATTRIBUTES);
  const onMakesChange = () => clearListAttributes(MAKE_DEPENDENT_ATTRIBUTES);

  // Switching the operator moves the chosen values to the other list, so nothing is lost.
  const handleListOperatorChange = (
    attribute: VehicleSelectionListAttribute,
    operator: VehicleSelectionOperator
  ) => {
    const {includeField, excludeField} = VEHICLE_SELECTION_LIST_ATTRIBUTES[attribute];
    const [sourceField, targetField] =
      operator === VehicleSelectionOperator.EXCLUDED
        ? [includeField, excludeField]
        : [excludeField, includeField];

    props.binding.setListFieldValue(targetField, props.filter?.[sourceField] ?? [], {
      shouldDirty: true,
    });
    props.binding.setListFieldValue(sourceField, [], {shouldDirty: true});
    setOperatorOverrides((current) => ({...current, [attribute]: operator}));
    // The model lists follow the included makes, as on a make change.
    if (attribute === 'MAKE') {
      onMakesChange();
    }
  };

  // The include side of the vehicle type is a single value, the exclude side a list.
  const handleVehicleTypeOperatorChange = (operator: VehicleSelectionOperator) => {
    if (operator === VehicleSelectionOperator.EXCLUDED) {
      props.binding.setListFieldValue(
        'excludedVehicleTypes',
        isNotNil(vehicleType) ? [vehicleType] : [],
        {
          shouldDirty: true,
        }
      );
      props.binding.setTextFieldValue('vehicleType', null, {shouldDirty: true});
    } else {
      props.binding.setTextFieldValue(
        'vehicleType',
        head(props.filter?.excludedVehicleTypes ?? []) ?? null,
        {shouldDirty: true}
      );
      props.binding.setListFieldValue('excludedVehicleTypes', [], {shouldDirty: true});
    }
    onVehicleTypeChange();
    setOperatorOverrides((current) => ({...current, [VEHICLE_TYPE_ATTRIBUTE]: operator}));
  };

  const isVehicleTypeDependentDisabled = props.isDisabled || filterOptions.isMissingVehicleType;
  const isMakeDependentDisabled = props.isDisabled || filterOptions.isMissingMakes;

  const selectVehicleTypeTooltip =
    !props.isDisabled && filterOptions.isMissingVehicleType
      ? i18n.t('entity.interest.tooltips.selectMake')
      : undefined;
  const selectMakeTooltip =
    !props.isDisabled && filterOptions.isMissingMakes
      ? i18n.t('entity.interest.tooltips.selectModel')
      : undefined;

  const missingOptions: Option[] =
    attributes
      ?.filter((item) => item.supportsMissing)
      .map((item) => ({
        value: item.attribute,
        label: getVehicleSelectionAttributeLabel(item.attribute),
      })) ?? [];

  const isVehicleTypeExcluded = vehicleTypeOperator === VehicleSelectionOperator.EXCLUDED;

  const vehicleTypeOperatorAction = {
    title: i18n.t('entity.promoPhotos.vehicleSelection.operators.anyOf'),
    leftIcon: 'action/swap_horiz' as const,
    isDisabled: props.isDisabled,
    onClick: () => handleVehicleTypeOperatorChange(VehicleSelectionOperator.EXCLUDED),
  };

  const renderListField = (
    attribute: VehicleSelectionListAttribute,
    presentation: ListFieldPresentation
  ) => {
    const {includeField, excludeField} = VEHICLE_SELECTION_LIST_ATTRIBUTES[attribute];
    const operator = getListOperator(attribute);

    return (
      <VehicleSelectionListField
        control={props.control}
        name={props.binding.getListFieldName(
          operator === VehicleSelectionOperator.EXCLUDED ? excludeField : includeField
        )}
        label={presentation.label}
        options={presentation.options}
        isLoading={presentation.isLoading}
        isDisabled={props.isDisabled || presentation.isDisabled}
        tooltip={presentation.tooltip}
        operator={canExclude(attribute) ? operator : null}
        onOperatorChange={(nextOperator) => handleListOperatorChange(attribute, nextOperator)}
        onChange={presentation.onChange}
        data-testid={suffixTestId(includeField, props)}
      />
    );
  };

  return (
    <Grid columns={2} spacing={4}>
      {renderListField('BRANCH', {
        label: i18n.t('entity.branch.labels.branch'),
        options: filterOptions.branchOptions,
        isLoading: filterOptions.isBranchesLoading,
      })}
      {renderListField('SALE_PRESET', {
        label: i18n.t('entity.vehicle.labels.salePreset'),
        options: filterOptions.salePresetOptions,
        isLoading: filterOptions.isSalePresetsLoading,
      })}

      <Hide when={isVehicleTypeExcluded}>
        <FormField
          control={props.control}
          type="choice"
          name={props.binding.getTextFieldName('vehicleType')}
          label={i18n.t('entity.vehicle.labels.type')}
          labelActions={
            canExclude(VEHICLE_TYPE_ATTRIBUTE) ? [vehicleTypeOperatorAction] : undefined
          }
          options={filterOptions.vehicleTypeOptions}
          isLoading={filterOptions.isCatalogueLoading}
          isDisabled={props.isDisabled}
          placeholder={i18n.t('general.labels.selectOption')}
          menuInPortal
          onChange={onVehicleTypeChange}
          data-testid={suffixTestId('vehicleType', props)}
        />
      </Hide>
      <Show when={isVehicleTypeExcluded}>
        <VehicleSelectionListField
          control={props.control}
          name={props.binding.getListFieldName('excludedVehicleTypes')}
          label={i18n.t('entity.vehicle.labels.type')}
          options={filterOptions.vehicleTypeOptions}
          isLoading={filterOptions.isCatalogueLoading}
          isDisabled={props.isDisabled}
          operator={canExclude(VEHICLE_TYPE_ATTRIBUTE) ? VehicleSelectionOperator.EXCLUDED : null}
          onOperatorChange={handleVehicleTypeOperatorChange}
          data-testid={suffixTestId('vehicleType', props)}
        />
      </Show>
      {renderListField('CONDITION', {
        label: i18n.t('entity.vehicle.labels.condition'),
        options: filterOptions.vehicleConditionOptions,
        isLoading: filterOptions.isCatalogueLoading,
        isDisabled: isVehicleTypeDependentDisabled,
        tooltip: selectVehicleTypeTooltip,
      })}

      {renderListField('MAKE', {
        label: i18n.t('entity.vehicle.labels.make'),
        options: filterOptions.makeOptions,
        isLoading: filterOptions.isMakeModelLoading,
        isDisabled: isVehicleTypeDependentDisabled,
        tooltip: selectVehicleTypeTooltip,
        onChange: onMakesChange,
      })}
      {renderListField('MODEL', {
        label: i18n.t('entity.vehicle.labels.model'),
        options: filterOptions.modelOptionsGroupedByMake,
        isLoading: filterOptions.isMakeModelLoading,
        isDisabled: isMakeDependentDisabled,
        tooltip: selectMakeTooltip,
      })}

      {renderListField('MODEL_FAMILY', {
        label: i18n.t('entity.vehicle.labels.modelFamily'),
        options: filterOptions.modelFamilyOptionsGroupedByMake,
        isLoading: filterOptions.isMakeModelLoading,
        isDisabled: isMakeDependentDisabled,
        tooltip: selectMakeTooltip,
      })}
      {renderListField('FUEL_TYPE', {
        label: i18n.t('entity.vehicle.labels.fuel'),
        options: filterOptions.fuelTypeOptions,
        isLoading: filterOptions.isCatalogueLoading,
        isDisabled: isVehicleTypeDependentDisabled,
        tooltip: selectVehicleTypeTooltip,
      })}

      {renderListField('BODY_STYLE', {
        label: i18n.t('entity.vehicle.labels.carStyle'),
        options: filterOptions.bodyStyleOptions,
        isLoading: filterOptions.isCatalogueLoading,
        isDisabled: isVehicleTypeDependentDisabled,
        tooltip: selectVehicleTypeTooltip,
      })}
      {renderListField('TAGS', {
        label: i18n.t('entity.salesCampaign.labels.vehicleTag'),
        options: filterOptions.tagOptions,
        isLoading: filterOptions.isTagsLoading,
      })}

      <VehicleSelectionPriceField
        control={props.control}
        name={props.binding.getNumberFieldName('priceFrom')}
        label={i18n.t('entity.salesCampaign.labels.priceFrom')}
        currency={tenantCurrency}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('priceFrom', props)}
      />
      <VehicleSelectionPriceField
        control={props.control}
        name={props.binding.getNumberFieldName('priceTo')}
        label={i18n.t('entity.salesCampaign.labels.priceTo')}
        currency={tenantCurrency}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('priceTo', props)}
      />

      <FormField
        control={props.control}
        type="integer"
        name={props.binding.getNumberFieldName('mileageFrom')}
        label={i18n.t('entity.vehicle.labels.mileageFrom')}
        suffix={mileageSuffix}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('mileageFrom', props)}
      />
      <FormField
        control={props.control}
        type="integer"
        name={props.binding.getNumberFieldName('mileageTo')}
        label={i18n.t('entity.vehicle.labels.mileageTo')}
        suffix={mileageSuffix}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('mileageTo', props)}
      />

      <FormField
        control={props.control}
        type="integer"
        name={props.binding.getNumberFieldName('yearFrom')}
        label={i18n.t('entity.salesCampaign.labels.yearFrom')}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('yearFrom', props)}
      />
      <FormField
        control={props.control}
        type="integer"
        name={props.binding.getNumberFieldName('yearTo')}
        label={i18n.t('entity.salesCampaign.labels.yearTo')}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('yearTo', props)}
      />

      <FormField
        control={props.control}
        type="integer"
        name={props.binding.getNumberFieldName('daysInStock')}
        label={i18n.t('entity.salesCampaign.labels.stockAgeDays')}
        helperText={i18n.t('entity.salesCampaign.hints.stockAgeDays')}
        suffix={daysSuffix}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('daysInStock', props)}
      />
      <FormField
        control={props.control}
        type="apiDate"
        name={props.binding.getTextFieldName('stockInDateFrom')}
        label={i18n.t('entity.salesCampaign.labels.stockInDateFrom')}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('stockInDateFrom', props)}
      />

      <Show when={props.areExtendedOperatorsEnabled}>
        <GridItem span={2}>
          <FormField
            control={props.control}
            type="multiChoice"
            name={props.binding.getListFieldName('missing')}
            label={i18n.t('entity.promoPhotos.vehicleSelection.operators.valueIsMissing')}
            helperText={i18n.t('entity.promoPhotos.vehicleSelection.labels.valueIsMissingHelper')}
            options={missingOptions}
            isDisabled={props.isDisabled}
            placeholder={i18n.t('general.labels.selectOption')}
            menuInPortal
            data-testid={suffixTestId('missing', props)}
          />
        </GridItem>
      </Show>
    </Grid>
  );
}
